import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Upload, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const HEADERS = ["full_name","email","phone","country","city","uci","passport_number","college","program","intake","admission_officer_email","college_app_id","priority"];
const SAMPLE = `full_name,email,phone,country,uci,college,program,intake,admission_officer_email
Aisha Khan,aisha@example.com,+1-416-555-0101,Pakistan,UCI-1000001,University of Niagara Falls (UNF),Master of Management,September 2026,admissions.demo@b2badmissions.test
Rohit Patel,rohit@example.com,+91-98-000-0002,India,UCI-1000002,IBU,MBA,January 2027,`;

function parseCsv(text) {
  const lines = text.split(/\r?\n/).map(l=>l.trim()).filter(Boolean);
  if (lines.length === 0) return { rows: [], headers: [] };
  const split = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
      else { cur += ch; }
    }
    out.push(cur); return out.map(s=>s.trim().replace(/^"|"$/g,""));
  };
  const headers = split(lines[0]).map(h=>h.toLowerCase().replace(/ /g,"_"));
  const rows = lines.slice(1).map(l => {
    const cells = split(l); const obj = {};
    headers.forEach((h,i) => obj[h] = cells[i] || "");
    return obj;
  });
  return { headers, rows };
}

export default function B2CImport() {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text(); setText(t); setPreview(parseCsv(t));
  };
  const parse = () => { const p = parseCsv(text); setPreview(p); if (!p.rows.length) toast.error("No data rows detected"); };
  const run = async () => {
    if (!preview?.rows?.length) return toast.error("Parse a CSV first");
    setBusy(true);
    try {
      const r = await api.post("/b2c/import/students", { rows: preview.rows });
      setResult(r.data); toast.success(`${r.data.created_students} students, ${r.data.created_applications} applications imported`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Bulk Student Import</h1>
        <p className="text-sm text-slate-500 mt-1">Upload or paste a CSV. Rows can create a Student, optionally with their first Application in one go.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">Accepted columns</div>
          <div className="flex flex-wrap gap-2">
            {HEADERS.map(h => <span key={h} className="mono text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1">{h}</span>)}
          </div>
          <p className="text-xs text-slate-500 mt-2">College and Program names must match Master Data exactly (or use College short code). Officer is looked up by email.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input type="file" accept=".csv,.txt" onChange={handleFile} data-testid="import-file-input" className="text-sm" />
          <Button variant="outline" data-testid="import-sample-btn" onClick={()=>{ setText(SAMPLE); setPreview(parseCsv(SAMPLE)); }}>Load sample</Button>
          <Button variant="outline" onClick={parse} data-testid="import-parse-btn">Parse CSV</Button>
          <Button data-testid="import-run-btn" onClick={run} disabled={busy || !preview?.rows?.length} className="bg-orange-500 hover:bg-orange-600 text-white ml-auto">
            <Upload className="w-4 h-4 mr-2"/>{busy ? "Importing…" : `Import ${preview?.rows?.length||0} row(s)`}
          </Button>
        </div>
        <Textarea data-testid="import-textarea" value={text} onChange={e=>setText(e.target.value)} placeholder="Paste CSV here (or use Upload)" className="min-h-40 mono text-xs" />
      </div>

      {preview && preview.rows.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 heading-font font-semibold flex items-center gap-2"><FileText className="w-4 h-4"/>Preview · {preview.rows.length} row(s)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>{preview.headers.map(h=><th key={h} className="text-left px-3 py-2 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    {preview.headers.map(h => <td key={h} className="px-3 py-2 whitespace-nowrap">{r[h]||"—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rows.length > 20 && <div className="px-4 py-2 text-xs text-slate-500">…and {preview.rows.length-20} more.</div>}
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700 heading-font font-semibold"><CheckCircle2 className="w-5 h-5"/>Import Complete</div>
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Rows" value={result.total_rows}/>
            <Stat label="Students Created" value={result.created_students}/>
            <Stat label="Applications Created" value={result.created_applications}/>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2">
              <div className="text-sm font-medium text-amber-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4"/>{result.errors.length} row(s) skipped</div>
              <ul className="mt-2 text-xs text-slate-600 space-y-1">
                {result.errors.slice(0, 10).map((e, i) => <li key={i} className="mono">Row {e.row}: {e.error}</li>)}
                {result.errors.length > 10 && <li>…and {result.errors.length-10} more.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-slate-50 rounded-lg p-3"><div className="text-xs uppercase text-slate-500">{label}</div><div className="heading-font text-xl font-bold text-slate-900">{value}</div></div>
);
